import type { SupabaseClient } from '@supabase/supabase-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { FilaCartera, MedioDePago, PagoDeCuenta } from '@/lib/supabase/tipos-bd'

/**
 * La cartera: cada servicio finalizado con lo que vale, lo pagado y el saldo
 * (fn_cartera, 0246). Un servicio entra solo, al finalizarse; aquí no se crea
 * nada, solo se anotan los pagos.
 */
export function useCartera() {
  return useQuery({
    queryKey: ['cartera'],
    queryFn: async (): Promise<FilaCartera[]> => {
      const { data, error } = await supabase.rpc('fn_cartera')
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as FilaCartera[]
    },
  })
}

export type PagoNuevo = {
  ordenServicioId: string
  valor: number
  fecha: string
  medio: MedioDePago
  referencia: string
  nota: string
  /** La FEV a la que va. Sin FEV, va a lo que se cobra con la cotización. */
  woIdAsientoFactura: number | null
}

/**
 * Anotar un pago. La base no deja pagar más que el saldo ni con fecha futura.
 * A una FEV, le pide a World Office el recibo de caja (0293).
 */
export function useRegistrarPago() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async (p: PagoNuevo): Promise<void> => {
      const { error } = await supabase.rpc('fn_registrar_pago', {
        p_orden_servicio_id: p.ordenServicioId,
        p_valor: p.valor,
        p_fecha: p.fecha,
        p_medio: p.medio,
        p_referencia: p.referencia.trim() || null,
        p_nota: p.nota.trim() || null,
        p_wo_id_asiento_factura: p.woIdAsientoFactura,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cartera'] })
      await cliente.invalidateQueries({ queryKey: ['pagos-de-cuenta'] })
      await cliente.invalidateQueries({ queryKey: ['pagos-del-periodo'] })
      // Lo recaudado también sale en el tablero.
      await cliente.invalidateQueries({ queryKey: ['el-periodo'] })
    },
  })
}

/**
 * Los pagos de una cuenta, del más reciente al más viejo (0293): los abonos
 * que World Office tiene a sus FEV y lo anotado en la app que WO todavía no
 * refleja. Solo se piden al abrirlos.
 */
export function usePagosDeCuenta(ordenServicioId: string, abiertos: boolean) {
  return useQuery({
    queryKey: ['pagos-de-cuenta', ordenServicioId],
    enabled: abiertos,
    queryFn: async (): Promise<PagoDeCuenta[]> => {
      const { data, error } = await supabase.rpc('fn_pagos_de_la_cuenta', {
        p_orden_servicio_id: ordenServicioId,
      })
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as PagoDeCuenta[]
    },
    // Un pago en camino a WO cambia solo: se mira de nuevo cada 15 segundos.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((p) => p.estado === 'en_camino') ? 15_000 : false,
  })
}

/** Quitar un pago que World Office rechazó: no cuenta, solo estorba. */
export function useQuitarPagoRechazado() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async (pagoId: string): Promise<void> => {
      const { error } = await supabase.rpc('fn_quitar_pago_rechazado', { p_pago_id: pagoId })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cartera'] })
      await cliente.invalidateQueries({ queryKey: ['pagos-de-cuenta'] })
    },
  })
}

/*
 * `configuracion` no está en los tipos generados; se lee sin tipos. La
 * política deja leerla a cualquiera con sesión.
 */
const sinTipos = supabase as unknown as SupabaseClient

/**
 * Si «Anotar pago» sobre una FEV ya crea el recibo de caja en World Office
 * (configuracion.recibos_a_wo, 0293). Apagado, esos pagos se hacen en WO.
 */
export function useRecibosAWo() {
  return useQuery({
    queryKey: ['configuracion', 'recibos_a_wo'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await sinTipos
        .from('configuracion')
        .select('valor')
        .eq('clave', 'recibos_a_wo')
        .maybeSingle()
      if (error) throw new Error(mensajeDeError(error))
      return (data as { valor?: string } | null)?.valor === 'true'
    },
    staleTime: 60_000,
  })
}

/**
 * LO RECAUDADO EN UN PERIODO, POR DÓNDE ENTRÓ.
 *
 * Johan: «¿cuánto recaudé en bancos o en efectivo?». Bancos es lo que pasa
 * por una cuenta —transferencia, consignación, cheque—; efectivo es lo que
 * se recibió en mano. «Otro» se cuenta aparte para que no se pierda.
 * Se cuenta por la fecha en que el cliente pagó, no por cuándo se anotó.
 */
export type RecaudoDelPeriodo = {
  total: number
  bancos: number
  efectivo: number
  otro: number
  pagos: number
}

/**
 * Sale de las mismas cifras del tablero (fn_cifras_del_rango, 0293): los
 * recibos de World Office a las FEV de los proyectos, lo de contado y lo
 * anotado en la app que WO todavía no refleja. Las notas crédito no son
 * plata que entró.
 */
export function usePagosDelPeriodo(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['pagos-del-periodo', desde, hasta],
    queryFn: async (): Promise<RecaudoDelPeriodo> => {
      const { data, error } = await sinTipos.rpc('fn_cifras_del_rango', {
        p_desde: desde,
        p_hasta: hasta,
      })
      if (error) throw new Error(mensajeDeError(error))
      const r = ((data ?? {}) as { recaudado?: Record<string, number | string> }).recaudado ?? {}
      return {
        total: Number(r.valor ?? 0),
        bancos: Number(r.bancos ?? 0),
        efectivo: Number(r.efectivo ?? 0),
        otro: Number(r.otro ?? 0),
        pagos: Number(r.cantidad ?? 0),
      }
    },
    placeholderData: (previo) => previo,
  })
}
